/**
 * Web personal itinerary editor (plan U5 — the Phase 1 user-facing artifact).
 *
 * Wires the serial per-itinerary mutation queue (D8) with optimistic updates
 * to the tRPC itinerary mutations, the day-panel drag-drop + keyboard reorder
 * (spec "Keyboard reorder / accessibility"), the travel-time pill (D4), the
 * auto-save status pill (D8), audience / archive / abandon controls.
 *
 * Layout is tabbed-by-day (#245): `ItineraryDayTabs` owns the day strip and
 * this component renders exactly one `ItineraryDayPanel` — the active day.
 * Stops cross days three ways, all funnelling into the same `moveStop` op:
 * dragging into another panel, dragging onto a day tab, and the stop card's
 * "Move to day…" menu. The itinerary title is inline-editable (#249) through
 * the existing `updateMetadata` procedure.
 *
 * This file is the composition layer only (#314): the header block lives in
 * `ItineraryEditorHeader`, and every queued mutation — with its `apply`
 * optimistic transform and `dependsOn` scope — lives in `useItineraryStopOps`
 * / `useItineraryPlanOps`. What stays here is the state the editor owns: the
 * active day, the keyboard grab, the ARIA live-region announcements, and the
 * pickers/modals.
 *
 * Form convention (repo, non-negotiable): action buttons TOP RIGHT, <div> not
 * <form>, onClick not onSubmit, PageContainer + Typography + Button + brand
 * tokens, NO in-page "Back to…" links.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import type { ItineraryWithViewFlags, SerializedItinerary } from './itineraryTypes';
import { useItineraryMutationQueue } from './useItineraryMutationQueue';
import { useItineraryStopOps } from './useItineraryStopOps';
import { useItineraryPlanOps } from './useItineraryPlanOps';
import { ItineraryDayPanel, type GrabState } from './ItineraryDayPanel';
import { ItineraryDayTabs, dayPanelId, dayTabId } from './ItineraryDayTabs';
import { ItineraryEditorHeader } from './ItineraryEditorHeader';
import { VenuePickerModal } from './VenuePickerModal';
import { PlacePickerModal } from './PlacePickerModal';
import { StopDetailsModal, type StopDraft } from './StopDetailsModal';
import { PageContainer } from '../PageContainer';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';

interface ItineraryEditorProps {
  slug: string;
  initial: ItineraryWithViewFlags;
}

interface StopPickerTarget {
  dayId: string;
}

export const ItineraryEditor = ({ slug, initial }: ItineraryEditorProps) => {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const refetch = useCallback(async (): Promise<
    SerializedItinerary | undefined
  > => {
    const fresh = await utils.itinerary.getBySlug.fetch({ slug });
    return fresh;
  }, [slug, utils]);

  const queue = useItineraryMutationQueue({ initial, refetch });
  const it = queue.itinerary;
  const itineraryId = it._id;

  const [grab, setGrab] = useState<GrabState | null>(null);
  const [announce, setAnnounce] = useState('');
  const [activeDayId, setActiveDayId] = useState<string | null>(
    it.days[0]?._id ?? null
  );

  // Sync active day with the days array. Cases handled:
  //   - New day appended → follow it (intent of "+ Add day").
  //   - Active day removed (length shrank) → fall back to the first day.
  //   - Active id swapped while length is unchanged (optimistic `tmp-…` id
  //     reconciled with the server-assigned id by the mutation queue) → match
  //     by index in the previous days array so the tab stays put.
  const prevDaysRef = useRef(it.days);
  useEffect(() => {
    const prev = prevDaysRef.current;
    const cur = it.days;
    const exists = activeDayId
      ? cur.some((d) => d._id === activeDayId)
      : false;
    if (cur.length > prev.length) {
      setActiveDayId(cur[cur.length - 1]._id);
    } else if (!exists && activeDayId) {
      if (cur.length === prev.length) {
        const idx = prev.findIndex((d) => d._id === activeDayId);
        setActiveDayId(cur[Math.max(0, idx)]?._id ?? null);
      } else {
        setActiveDayId(cur[0]?._id ?? null);
      }
    }
    prevDaysRef.current = cur;
  }, [it.days, activeDayId]);

  const [venuePicker, setVenuePicker] = useState<StopPickerTarget | null>(
    null
  );
  const [placePicker, setPlacePicker] = useState<StopPickerTarget | null>(
    null
  );
  const [showAbandon, setShowAbandon] = useState(false);
  const [stopDraft, setStopDraft] = useState<StopDraft | null>(null);

  const deleteOwn = trpc.itinerary.deleteOwn.useMutation();

  const enqueue = queue.enqueue;
  const lastQueued = useRef(0);

  // Every mutation below flows through the queue's `enqueue` (D8).
  const stopOps = useItineraryStopOps({ itineraryId, enqueue });
  const planOps = useItineraryPlanOps({ itineraryId, itinerary: it, enqueue });
  const moveStop = stopOps.moveStop;

  // ----- editor actions ----------------------------------------------------

  // Keyboard reorder: translate a direction into a moveStop + announce.
  const onKeyboardMove = (
    stopId: string,
    fromDayId: string,
    dir: 'up' | 'down' | 'left' | 'right'
  ) => {
    const dayIdx = it.days.findIndex((d) => d._id === fromDayId);
    if (dayIdx === -1) return;
    const day = it.days[dayIdx];
    const stopIdx = day.stops.findIndex((s) => s._id === stopId);
    if (stopIdx === -1) return;

    if (dir === 'up' || dir === 'down') {
      const target = dir === 'up' ? stopIdx - 1 : stopIdx + 1;
      if (target < 0 || target > day.stops.length - 1) return;
      moveStop(stopId, fromDayId, fromDayId, target);
      announceMove(stopId, fromDayId, target);
    } else {
      const targetDayIdx = dir === 'left' ? dayIdx - 1 : dayIdx + 1;
      if (targetDayIdx < 0 || targetDayIdx > it.days.length - 1) return;
      const toDay = it.days[targetDayIdx];
      const toOrdinal = toDay.stops.length;
      moveStop(stopId, fromDayId, toDay._id, toOrdinal);
      announceMove(stopId, toDay._id, toOrdinal);
      // Follow the grabbed stop into the destination day tab so the user can
      // see where it landed.
      setActiveDayId(toDay._id);
    }
  };

  const announceMove = (
    stopId: string,
    toDayId: string,
    toOrdinal: number
  ) => {
    const dIdx = it.days.findIndex((d) => d._id === toDayId);
    const dLabel =
      it.days[dIdx]?.title || `Day ${dIdx >= 0 ? dIdx + 1 : '?'}`;
    const total = (it.days[dIdx]?.stops.length ?? 0) || 1;
    setAnnounce(
      `Moved stop to position ${toOrdinal + 1} of ${total}, ${dLabel}`
    );
  };

  const handleReorderDay = (dayId: string, toOrdinal: number) => {
    planOps.reorderDay(dayId, toOrdinal);
    const label =
      it.days.find((d) => d._id === dayId)?.title ??
      `Day ${it.days.findIndex((d) => d._id === dayId) + 1}`;
    setAnnounce(
      `Moved ${label} to position ${toOrdinal + 1} of ${it.days.length}`
    );
  };

  // A stop dropped onto a day tab appends to that day. The tab strip has no
  // position to aim at, so "append" is the only sensible landing spot; a user
  // who wants an exact slot holds over the tab until it auto-activates and
  // drops into the panel instead.
  const handleDropStopOnDay = (
    stopId: string,
    fromDayId: string,
    toDayId: string
  ) => {
    if (fromDayId === toDayId) return;
    const toDay = it.days.find((d) => d._id === toDayId);
    if (!toDay) return;
    const toOrdinal = toDay.stops.length;
    moveStop(stopId, fromDayId, toDayId, toOrdinal);
    announceMove(stopId, toDayId, toOrdinal);
    setActiveDayId(toDayId);
  };

  // An unparseable duration aborts the save and leaves the modal open.
  const saveStopDetails = () => {
    if (!stopDraft) return;
    if (stopOps.saveStopDetails(stopDraft)) setStopDraft(null);
  };

  const handleAbandon = async () => {
    await deleteOwn.mutateAsync({ itineraryId });
    await utils.itinerary.listMine.invalidate();
    navigate('/itineraries');
  };

  const archived = it.editorialStatus === 'archived';

  void lastQueued;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer className="mt-2">
        <ItineraryEditorHeader
          title={it.title}
          description={it.description}
          audience={it.audience}
          archived={archived}
          status={queue.status}
          failure={queue.failure}
          onRetry={queue.retry}
          onDismiss={queue.dismiss}
          onSaveTitle={planOps.saveTitle}
          onSaveDescription={planOps.saveDescription}
          onFlipAudience={planOps.flipAudience}
          onToggleArchive={planOps.toggleArchive}
          onAbandon={() => setShowAbandon(true)}
        />

        {/* ARIA live region for keyboard reorder announcements. */}
        <div className="sr-only" role="status" aria-live="assertive">
          {announce}
        </div>

        <div className="mt-4">
          <ItineraryDayTabs
            days={it.days}
            activeDayId={activeDayId}
            onSelect={setActiveDayId}
            onDropStopOnDay={handleDropStopOnDay}
            onReorderDay={handleReorderDay}
            trailing={
              <Button
                size="sm"
                variant="outline"
                onClick={planOps.addDay}
                aria-label="Add day"
              >
                + Add day
              </Button>
            }
          />

          {it.days.map((day, di) => {
            if (day._id !== activeDayId) return null;
            return (
              <div
                key={day._id}
                role="tabpanel"
                id={dayPanelId(day._id)}
                aria-labelledby={dayTabId(day._id)}
                className="mt-3"
              >
                <ItineraryDayPanel
                  day={day}
                  dayIndex={di}
                  dayCount={it.days.length}
                  grab={grab}
                  onSetGrab={setGrab}
                  onMove={onKeyboardMove}
                  onDropStop={moveStop}
                  onAddVenue={() => setVenuePicker({ dayId: day._id })}
                  onAddPlace={() => setPlacePicker({ dayId: day._id })}
                  onEditDayTitle={() => planOps.renameDay(day._id)}
                  onEditDayDate={() => planOps.setDayDate(day._id)}
                  onRemoveDay={() => planOps.removeDay(day._id)}
                  onEditStopDetails={(stopId) => {
                    const s = day.stops.find((x) => x._id === stopId);
                    setStopDraft({
                      dayId: day._id,
                      stopId,
                      notes: s?.notes ?? '',
                      arrivalTime: s?.arrivalTime ?? '',
                      durationMinutes:
                        s?.durationMinutes != null
                          ? String(s.durationMinutes)
                          : '',
                    });
                  }}
                  onRemoveStop={(stopId) =>
                    stopOps.removeStop(day._id, stopId)
                  }
                  moveTargets={it.days
                    .map((d, i) => ({
                      id: d._id,
                      label: d.title || `Day ${i + 1}`,
                    }))
                    .filter((d) => d.id !== day._id)}
                  onMoveStopToDay={(stopId, toDayId) =>
                    handleDropStopOnDay(stopId, day._id, toDayId)
                  }
                />
              </div>
            );
          })}
        </div>
      </PageContainer>

      {venuePicker && (
        <VenuePickerModal
          title="Add a winery"
          onPick={(v) => {
            stopOps.addStopVenue(venuePicker.dayId, v);
            setVenuePicker(null);
          }}
          onClose={() => setVenuePicker(null)}
        />
      )}

      {placePicker &&
        (() => {
          // Bias place search toward the day's last place stop, if any, so a
          // chain (e.g. "Gott's") surfaces the branch nearest the trip.
          const day = it.days.find((d) => d._id === placePicker.dayId);
          const lastPlace = [...(day?.stops ?? [])]
            .reverse()
            .find((s) => s.refType === 'place' && s.place);
          const bias = lastPlace?.place
            ? { lat: lastPlace.place.lat, lng: lastPlace.place.lng }
            : undefined;
          return (
            <PlacePickerModal
              title="Add a place"
              bias={bias}
              onPick={(p) => {
                stopOps.addStopPlace(placePicker.dayId, p);
                setPlacePicker(null);
              }}
              onClose={() => setPlacePicker(null)}
            />
          );
        })()}

      {stopDraft && (
        <StopDetailsModal
          draft={stopDraft}
          onChange={setStopDraft}
          onSave={saveStopDetails}
          onCancel={() => setStopDraft(null)}
        />
      )}

      <ConfirmationModal
        isOpen={showAbandon}
        onClose={() => setShowAbandon(false)}
        onConfirm={handleAbandon}
        title="Abandon this itinerary?"
        message="This permanently deletes the itinerary and all its days and stops. This cannot be undone."
        confirmText="Abandon"
        cancelText="Keep"
        isDestructive
        isLoading={deleteOwn.isPending}
      />
    </div>
  );
};
